import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HiOutlineBell } from 'react-icons/hi';
import clsx from 'clsx';
import Icon from './Icon';
import { RootState } from '@/store';
import { ROUTES } from '@/routes';
import { useCloseOnOutsideClick } from '@/hooks/useCloseOnOutsideClick';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/services/notificationService';
import { downloadTenantAlertReport } from '@/services/adminAlertsService';
import type { AppNotification, NotificationsPage } from '@/types/notification.types';

export const NOTIFICATIONS_POLL_MS = 60_000;
const NOTIFICATIONS_PAGE_SIZE = 20;
const EMPTY_PAGE: NotificationsPage = { items: [], unread_count: 0 };

type BellRole = 'ADMIN' | 'SUPER_USER';

const formatNotificationTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const useNotificationsFeed = () => {
  const [page, setPage] = useState<NotificationsPage>(EMPTY_PAGE);

  const refresh = useCallback(async () => {
    try {
      setPage(await listNotifications({ limit: NOTIFICATIONS_PAGE_SIZE }));
    } catch {
      return;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh();
    }, NOTIFICATIONS_POLL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh]);

  return { page, setPage };
};

const markLocallyRead = (page: NotificationsPage, id: string): NotificationsPage => ({
  items: page.items.map(item => (item.id === id ? { ...item, read: true } : item)),
  unread_count: Math.max(0, page.unread_count - 1),
});

const NotificationBellPanel = ({ role }: { role: BellRole }) => {
  const navigate = useNavigate();
  const { page, setPage } = useNotificationsFeed();
  const [isOpen, setIsOpen] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useCloseOnOutsideClick(containerRef, close);

  const openAlert = async (notification: AppNotification) => {
    if (notification.type !== 'CONVERSATION_ALERT' || !notification.ref_id) return;
    if (role === 'SUPER_USER') {
      setIsOpen(false);
      navigate(ROUTES.SUPER_ADMIN_ALERTS);
      return;
    }
    try {
      await downloadTenantAlertReport(notification.ref_id);
    } catch {
      setDownloadFailed(true);
    }
  };

  const handleSelect = async (notification: AppNotification) => {
    setDownloadFailed(false);
    if (!notification.read) {
      const marked = await markNotificationRead(notification.id).then(
        () => true,
        () => false
      );
      if (marked) setPage(current => markLocallyRead(current, notification.id));
    }
    await openAlert(notification);
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setPage(current => ({
        items: current.items.map(item => ({ ...item, read: true })),
        unread_count: 0,
      }));
    } catch {
      return;
    }
  };

  const unread = page.unread_count;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none transition-colors flex items-center justify-center"
        aria-label="Notifications"
      >
        <Icon icon={HiOutlineBell} className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            translate="no"
            className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-[10px] font-bold leading-[1.1rem] text-white text-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            <button
              type="button"
              onClick={() => void handleMarkAll()}
              disabled={unread === 0}
              className="text-xs font-medium text-blue-600 hover:text-blue-500 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Mark all as read
            </button>
          </div>
          {downloadFailed && (
            <p className="px-4 py-2 text-xs text-red-600" role="alert">
              The report could not be downloaded. Try again in a few minutes.
            </p>
          )}
          {page.items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet.</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {page.items.map(item => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => void handleSelect(item)}
                    className={clsx(
                      'block w-full text-left px-4 py-3 hover:bg-gray-50',
                      !item.read && 'bg-blue-50/60'
                    )}
                  >
                    <span className="flex items-start gap-2">
                      {!item.read && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                      )}
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900">{item.title}</span>
                        {item.body && (
                          <span className="text-xs text-gray-600 line-clamp-2">{item.body}</span>
                        )}
                        <span className="mt-1 text-[11px] text-gray-400">
                          {item.tenant_code && <span translate="no">{item.tenant_code} · </span>}
                          {formatNotificationTime(item.created_at)}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const NotificationBell = () => {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  if (role !== 'ADMIN' && role !== 'SUPER_USER') return null;
  return <NotificationBellPanel role={role} />;
};

export default NotificationBell;
